import React from 'react';
import { ColorFrom, Feature } from '@tencent/workbench';
import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import BaseDrawerEffectFeature from '@framework/workbench/common/feature/base-drawer-effect-feature';
import ShadowPresetMenu from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/shadow-preset-menu';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import PresetPanel from '@framework/workbench/drawer/components/preset-panel';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IShadowModelVal } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { PresetUtilFactory } from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/preset-util/preset-util-facotry';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { EMPTY_EFFECT } from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/preset-util/base-preset-util';
import { ColorSourceType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import { IShadowFeature } from '@tencent/drawing-drawer-components';

const defaultShadowProps = {
    color: '#000000',
    tdColor: new TDColor({ rgbaStr: '#000000' }),
    opacity: 0,
    size: 0,
    blurRad: 0,
    dir: 0,
    dist: 0,
    rotWithShape: 0,    // 外阴影该值默认为0
    isOuter: true, /** 默认是外阴影 */
};

@Feature()
export class ShadowFeature extends BaseDrawerEffectFeature implements IShadowFeature {
    public shadowProps: IShadowModelVal = { ...defaultShadowProps };
    public get shadowInfo() {
        const shadow = (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .getShadow();
        return shadow && Object.keys(shadow).length > 0 ? shadow : defaultShadowProps;
    }

    public get isMultiDifferent(): boolean {
        return this.isMultiDiffValue('effect.shadow');
    }

    public get size() {
        return this.shadowProps?.size;
    }
    public get blur() {
        return this.shadowProps?.blurRad;
    }
    public get dist() {
        return this.shadowProps?.dist;
    }
    public get opacity() {
        return Math.round(Number(this.shadowProps?.opacity));
    }
    protected previewInfo = { ...this.shadowInfo };

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.SHADOW);
    }

    public initPropsCallback = () => {
        this.shadowProps = { ...this.shadowInfo };
    };

    public renderPreset() {
        const { iconStyle, iconName } = PresetUtilFactory.getPresetUtil(ActionType.SHADOW)
            .getPresetStyle(this.shadowInfo);
        return <PresetPanel
            src={iconStyle}
            hasEffect={iconName !== EMPTY_EFFECT}
            isMultiDifferent={this.isMultiDifferent}
            onVisibleChange={this.handlePresetVisibleChange}
            placement={'top'}
        >
            <ShadowPresetMenu
                preset={{ style: iconName }}
                onShadowPresetStyleClick={shadow => this.handleShadowPresetStyleClick(shadow)}
                onShadowPresetStyleMouseEnter={shadow => this.handleShadowPresetSytleEnter(shadow)}
                onShadowPresetStyleMouseLeave={() => this.handleShadowPresetStyleMouseLeave()} />
        </PresetPanel>;
    }

    public updatePreviewInfo = (color?) => {
        this.previewInfo = { ...this.shadowProps, color };
    };

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.SHADOW)
    public handleShadowChange(value, dragEnd?: boolean, from?: NumberInputFrom | AngleFrom, max?: number) {
        const shadowObj = this.getValidValue(value, from, max);
        const key = value && Object.keys(value)[0];
        if (key === 'size') {
            shadowObj.sizeX = shadowObj.value;
            shadowObj.sizeY = shadowObj.value;
        } else if (key === 'opacity') {
            this.shadowInfo.tdColor?.updateOpacity?.(value?.opacity);
        } else if (key === 'dir') {
            shadowObj.dir = shadowObj.dir % 360;
        }
        this.shadowProps = { ...this.shadowInfo, ...shadowObj };
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        this.updateShadowProps(onlyChangeUI);
        this.handleShadowReport(shadowObj, from, onlyChangeUI);
    }

    public updateShadowProps = (onlyChangeUI?: boolean, onPreview?: boolean) => {
        if (this.checkSelectType() <= 1) {
            this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        }
        const shadow: IShadowModelVal = onPreview ? { ...this.previewInfo } : (this.shadowProps
            ? { ...this.shadowProps } : this.shadowProps);
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .updateShadow(shadow, !!onlyChangeUI);
    };

    /**
     * 从‘预设’面板切换不同的阴影类型
     * @param {object} value 阴影的属性
     */
    public handleShadowPresetStyleClick = (shadow) => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.shadowProps = { ...defaultShadowProps, ...{ algn: null }, ...shadow };
        this.updateShadowProps();
        this.handleShadowPresetReport(shadow);
    };

    public handleShadowPresetSytleEnter = (shadow) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .updateShadow(shadow, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
    };

    public handleShadowPresetStyleMouseLeave = () => {
        if (this.checkSelectType() > 1) {
            return;
        }
        const shadow = { ...this.shadowProps };
        shadow.sizeX = shadow.sizeX || 0;
        shadow.sizeY = shadow.sizeY || 0;
        shadow.skewX = shadow.skewX || 0;
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel)?.getEffect?.()
            .updateShadow(shadow, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    public handleColorChange = (options) => {
        this.shadowProps = { ...this.shadowInfo, ...options };
        this.updateShadowProps();
    };

    public handlePresetVisibleChange = (visible: boolean) => {
        if (visible) {
            this.shadowProps = { ...this.shadowInfo };
        }
    };

    public handleSelectColorChange = (color: string, from) => {
        this.handleShadowColorChange(new TDColor({ rgbaStr: color }), from);
    };

    public handleShadowColorChange = (tdColor: ITDColor, from: ColorFrom | ColorSourceType) => {
        // const options = this.getColorOptions(tdColor.srgb_color);
        const options = {
            color: tdColor.srgb_color,
            tdColor,
            opacity: alphaToOpacity(tdColor.alpha),
        };
        this.handleColorChange(options);
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: tdColor.srgb_color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_SHADOW,
        });
    };

    // hover 到颜色上时预览属性
    public handleColorEnter = (color) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        this.previewInfo = { ...this.shadowProps, color };
        this.updateShadowProps(true, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
    };

    public handlePaletteLeave = () => {
        if (this.checkSelectType() > 1) {
            return;
        }
        this.previewInfo = { ...this.shadowProps };
        this.updateShadowProps(true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    private handleShadowPresetReport = (shadow) => {
        let ver5;
        if (!shadow) {
            ver5 = 1;
        } else {
            ver5 = shadow?.isOuter ? 2 : 3;
        }
        this.tdwDrawerReport(FormItemName.SHADOW, { ver5 });
    };

    private handleShadowReport = (value, from?: any, onlyChangeUI?: boolean) => {
        if (typeof value === 'number') {
            this.tdwDrawerReportAngle(FormItemName.SHADOW_ANGLE, from, value, onlyChangeUI);
        } else {
            let reportId;
            let ver6;
            if (value?.size) {
                reportId  = FormItemName.SHADOW_SIZE;
                ver6 = value.size;
            }
            if (value?.blurRad) {
                reportId  = FormItemName.SHADOW_BLUR;
                ver6 = value.blurRad;
            }
            if (value?.dist) {
                reportId  = FormItemName.SHADOW_DISTANCE;
                ver6 = value.dist;
            }
            if (value?.opacity) {
                reportId  = FormItemName.SHADOW_OPICITY;
                ver6 = value.opacity;
            }
            reportId && this.tdwDrawerReportNumberInput(reportId, { ver6 }, from, onlyChangeUI);
        }
    };
}
